// src/components/Sidebar.jsx
import React, { useState, useRef, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome,
  faChartLine,
  faUser, 
  faCog, 
  faQuestionCircle,
  faFlag,
  faSignOutAlt,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { ThemeContext } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';

const Sidebar = () => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const sidebarRef = useRef(null);
  const { isDarkMode } = useContext(ThemeContext);
  const { isExpanded, setIsExpanded } = useSidebar();

  const isActive = (path) => {
    if (path === '/analysis') {
      // Check if we're in any analysis-related route
      return location.pathname.startsWith('/analysis');
    }
    return location.pathname === path;
  };

  const handleClick = () => {
    setIsExpanded(false);
    setHoveredItem(null);
  };

  const menuItems = [
    {
      path: '/',
      icon: faHome,
      label: 'Home'
    },
    {
      path: '/analysis',
      icon: faChartLine,
      label: 'Analysis'
    },
    {
      path: '/profile',
      icon: faUser,
      label: 'Profile'
    },
    {
      path: '/settings',
      icon: faCog,
      label: 'Settings'
    }
  ];

  return (
    <div className="relative h-screen">
      <div 
        ref={sidebarRef}
        className={`absolute inset-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col justify-between z-50 ${
          isExpanded ? 'w-[140px]' : 'w-12'
        }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          setIsExpanded(false);
          setHoveredItem(null);
        }}
      >
        <ul className="mt-4">
          {menuItems.map((item) => (
            <li key={item.path} 
                className="group/item relative" 
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={(e) => {
                  // Check if we're not hovering over a child element
                  const rect = e.currentTarget.getBoundingClientRect();
                  const isLeavingToChild = e.clientY >= rect.top && 
                                         e.clientY <= rect.bottom && 
                                         e.clientX >= rect.left;
                  if (!isLeavingToChild) {
                    setHoveredItem(null);
                  }
                }}
            >
              <Link 
                to={item.path} 
                className={`flex items-center p-2 whitespace-nowrap hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                  isActive(item.path) ? 'bg-gray-100 dark:bg-gray-700' : ''
                }`}
                onClick={handleClick}
              >
                <div className="relative inline-block">
                  <FontAwesomeIcon icon={item.icon} className="text-gray-700 dark:text-gray-200 fa-fw fa-lg" />
                </div>
                <span className={`ml-2 text-gray-700 dark:text-gray-200 transition-opacity duration-300 pointer-events-none flex-grow ${
                  isExpanded ? 'opacity-100' : 'opacity-0'
                }`}>
                  {item.label}
                </span>
                {item.subItems && isExpanded && (
                  <FontAwesomeIcon 
                    icon={hoveredItem === item.path ? faChevronRight : faChevronDown} 
                    className="text-gray-400 dark:text-gray-500 text-xs ml-1 transition-transform duration-200"
                  />
                )}
              </Link>
              {item.subItems && hoveredItem === item.path && isExpanded && (
                <ul className="ml-6">
                  {item.subItems.map((subItem) => (
                    <li key={subItem.path}>
                      <Link 
                        to={subItem.path} 
                        className={`flex items-center py-1.5 px-2 whitespace-nowrap hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                          location.pathname === subItem.path ? 'bg-gray-100 dark:bg-gray-700' : ''
                        }`}
                        onClick={handleClick}
                      >
                        <div className="relative inline-block">
                          <FontAwesomeIcon icon={subItem.icon} className="text-gray-500 dark:text-gray-400 fa-fw text-sm" />
                          {subItem.badge && (
                            <span className="absolute -top-1 -right-1 transform translate-x-1/3 translate-y-0 bg-yellow-400 dark:bg-yellow-500 text-black dark:text-white text-[8px] font-medium leading-none min-w-[12px] h-[12px] flex items-center justify-center rounded-full px-[2px]">
                              {subItem.badge}
                            </span>
                          )}
                        </div>
                        <span className={`ml-2 text-gray-500 dark:text-gray-400 text-xs transition-opacity duration-300 pointer-events-none ${
                          isExpanded ? 'opacity-100' : 'opacity-0'
                        }`}>
                          {subItem.label}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <ul className="mb-4">
          <li>
            <Link 
              to="/logout" 
              className="flex items-center p-2 whitespace-nowrap hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              onClick={handleClick}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-gray-700 dark:text-gray-200 fa-fw fa-lg" />
              <span className={`ml-2 text-gray-700 dark:text-gray-200 transition-opacity duration-300 pointer-events-none ${
                isExpanded ? 'opacity-100' : 'opacity-0'
              }`}>
                Logout
              </span>
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
